import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { adminAPI, locationsAPI, seriesAPI } from '../../../api/bookings';
import { bruneiToday } from '../../../lib/brunei';

const API = process.env.REACT_APP_BACKEND_URL;
const todayISO = () => bruneiToday();
// Back-dating cap for Manual mode only (June 2026). Recurring + Block Court
// keep the today-or-later lock. Cap = 30 days in Brunei time.
const thirtyDaysAgoISO = () => {
  const d = new Date(bruneiToday());
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
};

export function useManualBooking() {
  const navigate = useNavigate();

  const [mode, setMode] = useState('manual_booking');

  // Common booking fields
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [bookingDate, setBookingDate] = useState(todayISO());
  const [timeSlot, setTimeSlot] = useState('');
  const [duration, setDuration] = useState(1);
  const [sport, setSport] = useState('badminton');
  const [quantity, setQuantity] = useState(1);

  // Court availability + selection
  const [availableCourts, setAvailableCourts] = useState([]);
  const [allCourtsCount, setAllCourtsCount] = useState(0);
  const [selectedCourts, setSelectedCourts] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);

  // Customer (manual_booking only)
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  // Payment (manual_booking only)
  // Default to 'pay_later' so staff explicitly opt in to confirming
  // collection — avoids accidentally marking a booking paid in Cash
  // when the customer hasn't actually paid yet. The PaymentDiscountCard
  // effect snaps non-allowed methods back to 'cash' when the form is in
  // restricted (recurring-series) mode, so this default is safe there.
  const [paymentMethod, setPaymentMethod] = useState('pay_later');
  const [paymentReference, setPaymentReference] = useState('');

  // Discount (manual_booking only)
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');

  // Block-court fields
  const [blockLabel, setBlockLabel] = useState('MAINTENANCE');
  const [blockReason, setBlockReason] = useState('');

  // Recurrence (series mode only)
  const [pattern, setPattern] = useState('weekly');
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [endMode, setEndMode] = useState('end_date');
  const [endDate, setEndDate] = useState('');
  const [endAfterCount, setEndAfterCount] = useState('');
  const [skipHolidays, setSkipHolidays] = useState(true);
  const [skipFridayPrayer, setSkipFridayPrayer] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const toggleDay = useCallback((iso) => {
    setDaysOfWeek((prev) => prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso].sort());
  }, []);

  // Pricing
  const [unitPrice, setUnitPrice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Notify customer flag — Manual mode only. Defaults ON. Staff opts out
  // when back-dating (customer already knows they played) or when the
  // customer notified you in person and doesn't need an email receipt.
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  // Auto-detect back-dating (Brunei tz). Used to (a) show a soft warning
  // inline, (b) let the parent auto-toggle notify off if we choose to
  // (currently: we don't — always let staff decide explicitly).
  const isBackdated = mode === 'manual_booking'
    && bookingDate && bookingDate < todayISO();

  // ---------- effects ----------
  useEffect(() => {
    (async () => {
      try {
        const [locs, custs] = await Promise.all([
          locationsAPI.getAllLocations(),
          adminAPI.getCustomers(),
        ]);
        setLocations(locs || []);
        setCustomers(custs || []);
        if (locs && locs.length) setLocationId((prev) => prev || locs[0].id);
      } catch (e) {
        toast.error('Failed to load setup data');
      }
    })();
  }, []);

  useEffect(() => {
    if (!locationId || !bookingDate || !timeSlot) {
      setAvailableCourts([]);
      setAllCourtsCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingAvail(true);
        const res = await axios.get(
          `${API}/api/admin/available-court-numbers/${locationId}/${bookingDate}/${encodeURIComponent(timeSlot)}?duration=${duration}`
        );
        if (cancelled) return;
        const avail = res.data?.available_courts || [];
        setAvailableCourts(avail);
        setAllCourtsCount(res.data?.total_courts || 0);
        setSelectedCourts((prev) => prev.filter((n) => avail.includes(n)));
      } catch (e) {
        if (!cancelled) {
          setAvailableCourts([]);
          setAllCourtsCount(0);
        }
      } finally {
        if (!cancelled) setLoadingAvail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [locationId, bookingDate, timeSlot, duration]);

  useEffect(() => {
    if (mode === 'block_court' || !locationId || !bookingDate || !timeSlot || !sport) {
      setUnitPrice(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/pricing/calculate?location_id=${locationId}&date=${bookingDate}&time_slot=${encodeURIComponent(timeSlot)}&sport=${sport}&duration=${duration}`
        );
        if (!cancelled) {
          // Backend returns { price_per_hour, duration, total, ... }.
          // Store the PER-HOUR rate so the frontend can transparently
          // multiply by (quantity × duration) — avoids the double-multiply
          // bug where reading `total` and then multiplying by duration
          // again inflated 2h bookings from $28 to $56.
          const perHour = res.data?.price_per_hour;
          const fallbackPerHour =
            res.data?.total != null && duration > 0
              ? Number(res.data.total) / duration
              : null;
          setUnitPrice(perHour ?? fallbackPerHour ?? null);
        }
      } catch {
        if (!cancelled) setUnitPrice(null);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, locationId, bookingDate, timeSlot, sport, duration]);

  // ---------- derived ----------
  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return [];
    return (customers || [])
      .filter((c) => `${c.name || ''} ${c.email || ''} ${c.phone || ''}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [customers, customerSearch]);

  const courtsRequested = useMemo(() => [{ sport, quantity }], [sport, quantity]);
  const courtsAssigned = useMemo(
    () => selectedCourts.map((n) => ({ sport, court_number: n })),
    [selectedCourts, sport]
  );

  const grossTotal = useMemo(() => {
    if (mode === 'block_court' || !unitPrice) return 0;
    return Number(unitPrice) * quantity * duration;
  }, [mode, unitPrice, quantity, duration]);

  const discountAmount = useMemo(() => {
    if (mode === 'block_court' || !grossTotal) return 0;
    const v = parseFloat(discountValue || '0');
    if (!v) return 0;
    return discountType === 'percentage'
      ? Math.min(grossTotal, grossTotal * (Math.min(100, v) / 100))
      : Math.min(grossTotal, v);
  }, [mode, grossTotal, discountValue, discountType]);

  const finalTotal = Math.max(0, grossTotal - discountAmount);

  // ---------- handlers ----------
  const toggleCourt = useCallback((n) => {
    setSelectedCourts((prev) => {
      if (prev.includes(n)) return prev.filter((x) => x !== n);
      if (prev.length >= quantity) {
        toast.info(`You requested ${quantity} court${quantity > 1 ? 's' : ''}; deselect one first.`);
        return prev;
      }
      return [...prev, n];
    });
  }, [quantity]);

  const changeQuantity = useCallback((q) => {
    setQuantity(q);
    setSelectedCourts([]);
  }, []);

  const pickCustomer = useCallback((c) => {
    setSelectedCustomer(c);
    setCustomerSearch('');
  }, []);

  // The 5 functions below (validate, buildSeriesPayload, openSeriesPreview,
  // confirmSeriesCommit, handleSubmit) are NOT wrapped in useCallback by
  // design. They're called only from button onClick handlers; their
  // children don't memoize; and they previously required 15-22 entry
  // dependency arrays that were a maintenance hazard (Code Quality #6).
  // Plain function expressions capture state without dependency tracking.

  const validate = () => {
    if (!locationId || !bookingDate || !timeSlot) return 'Pick location, date, and time slot.';
    if (selectedCourts.length === 0) return 'Pick at least one court.';
    if (selectedCourts.length !== quantity) {
      return `You requested ${quantity} court(s) but selected ${selectedCourts.length}.`;
    }
    if (mode === 'manual_booking' || mode === 'series') {
      if (!selectedCustomer && (!guestName.trim() || !guestPhone.trim())) {
        return 'Pick a customer or fill guest name + phone.';
      }
      if (!paymentMethod) return 'Pick a payment method.';
    } else if (!blockLabel) {
      return 'Pick a block label.';
    }
    if (mode === 'series') {
      if (!['cash', 'complimentary', 'pay_later'].includes(paymentMethod)) {
        return 'Series MVP supports cash, complimentary, or pay later (hold) only.';
      }
      if (daysOfWeek.length === 0) return 'Pick at least one day of the week.';
      if (endMode === 'end_date' && !endDate) return 'Pick a series end date.';
      if (endMode === 'end_after_count') {
        const n = parseInt(endAfterCount, 10);
        if (!n || n < 2 || n > 52) return 'Enter 2–52 occurrences.';
      }
    }
    return null;
  };

  const buildSeriesPayload = () => {
    const recurrence = {
      pattern,
      days_of_week: daysOfWeek,
      start_date: bookingDate,
      skip_brunei_holidays: skipHolidays,
      skip_friday_prayer: skipFridayPrayer,
    };
    if (endMode === 'end_date') recurrence.end_date = endDate;
    else recurrence.end_after_count = parseInt(endAfterCount, 10);

    return {
      location_id: locationId,
      time_slot: timeSlot,
      duration,
      courts_requested: courtsRequested,
      courts_assigned: courtsAssigned,
      customer: selectedCustomer
        ? { user_id: selectedCustomer.id }
        : { guest: { name: guestName.trim(), phone: guestPhone.trim(), email: guestEmail.trim() || null } },
      payment_method: paymentMethod,
      payment_reference: paymentReference.trim() || null,
      discount: discountValue && parseFloat(discountValue) > 0
        ? { type: discountType, value: parseFloat(discountValue), reason: discountReason.trim() || null }
        : null,
      special_requests: '',
      recurrence,
    };
  };

  const openSeriesPreview = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    try {
      const data = await seriesAPI.previewSeries(buildSeriesPayload());
      setPreviewData(data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to preview series';
      setPreviewError(typeof msg === 'string' ? msg : 'Failed to preview series');
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmSeriesCommit = async () => {
    try {
      setSubmitting(true);
      const res = await seriesAPI.createSeries(buildSeriesPayload());
      toast.success(`Series created · ${res.created} booking${res.created === 1 ? '' : 's'}`);
      setPreviewOpen(false);
      navigate('/admin/bookings/series');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to create series';
      toast.error(typeof msg === 'string' ? msg : 'Failed to create series');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (mode === 'series') {
      await openSeriesPreview();
      return;
    }
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSubmitting(true);
      const base = {
        mode,
        location_id: locationId,
        booking_date: bookingDate,
        time_slot: timeSlot,
        duration,
        courts_assigned: courtsAssigned,
      };

      let payload;
      if (mode === 'manual_booking') {
        payload = {
          ...base,
          courts_requested: courtsRequested,
          customer: selectedCustomer
            ? { user_id: selectedCustomer.id }
            : { guest: { name: guestName.trim(), phone: guestPhone.trim(), email: guestEmail.trim() || null } },
          payment_method: paymentMethod,
          payment_reference: paymentReference.trim() || null,
          discount: discountValue && parseFloat(discountValue) > 0
            ? { type: discountType, value: parseFloat(discountValue), reason: discountReason.trim() || null }
            : null,
          // Staff-controlled: opt-out of the confirmation email (used
          // heavily when back-dating — customer already knows they played).
          notify_customer: notifyCustomer,
        };
      } else {
        payload = {
          ...base,
          block_label: blockLabel,
          block_reason: blockReason.trim() || null,
        };
      }

      const res = await adminAPI.createManualBooking(payload);
      const b = res.booking;
      toast.success(
        mode === 'manual_booking'
          ? `Booking confirmed · #${(b.id || '').slice(0, 8).toUpperCase()}`
          : `Court blocked · ${b.guest_name}`
      );
      navigate(`/admin/bookings?ref=${(b.id || '').slice(0, 8)}&status=CONFIRMED`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to save';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    // mode
    mode, setMode,
    isManual: mode === 'manual_booking',
    isSeries: mode === 'series',
    // slot / date / duration / sport / quantity
    locations, locationId, setLocationId,
    bookingDate, setBookingDate, todayISO, thirtyDaysAgoISO,
    isBackdated,
    notifyCustomer, setNotifyCustomer,
    timeSlot, setTimeSlot,
    duration, setDuration,
    sport, setSport,
    quantity, changeQuantity,
    // courts
    availableCourts, allCourtsCount, selectedCourts, loadingAvail, toggleCourt,
    // customer
    selectedCustomer, setSelectedCustomer,
    customerSearch, setCustomerSearch,
    filteredCustomers, pickCustomer,
    guestName, setGuestName, guestPhone, setGuestPhone, guestEmail, setGuestEmail,
    // payment
    paymentMethod, setPaymentMethod,
    paymentReference, setPaymentReference,
    // discount
    discountType, setDiscountType,
    discountValue, setDiscountValue,
    discountReason, setDiscountReason,
    // block
    blockLabel, setBlockLabel,
    blockReason, setBlockReason,
    // recurrence
    pattern, setPattern,
    daysOfWeek, toggleDay,
    endMode, setEndMode,
    endDate, setEndDate,
    endAfterCount, setEndAfterCount,
    skipHolidays, setSkipHolidays,
    skipFridayPrayer, setSkipFridayPrayer,
    previewOpen, setPreviewOpen,
    previewLoading, previewData, previewError,
    confirmSeriesCommit,
    // derived / submit
    unitPrice,
    grossTotal, discountAmount, finalTotal,
    submitting, handleSubmit,
  };
}
